import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { FileSpreadsheet, FileText, Loader2, Plus, Trash2, Pencil, Youtube, TriangleAlert, ExternalLink, Search } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgPromotionApi } from '@/services/org-promotion.api';
import {
  CreateOrgLessonTopicData,
  OrgLessonTopicRow,
  OrgReportsApi,
  UpdateOrgLessonTopicData,
} from '@/services/org-reports.api';
import { CreateLessonFileData, LessonFile } from '@/services/academic.api';
import { UploadApi } from '@/services/school.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { DataTable, useDataTable } from '@/components/data-table';

const columnHelper = createColumnHelper<OrgLessonTopicRow>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

// ───── School → Class → Section → Subject cascading picker (org tier) ───────
// Same as OrgLessonsPage's — duplicated locally per this codebase's own
// page-local-component convention (CreateTopicForm/FilesManager etc. are
// page-local in the school-tier LessonsPage.tsx too, not shared exports).
function OrgClassSectionSubjectPicker({
  schoolId,
  classId,
  onClassChange,
  sectionId,
  onSectionChange,
  classSubjectId,
  onSubjectChange,
}: {
  schoolId: string;
  classId: string;
  onClassChange: (v: string) => void;
  sectionId: string;
  onSectionChange: (v: string) => void;
  classSubjectId: string;
  onSubjectChange: (v: string) => void;
}) {
  const { data: classes = [] } = useQuery({
    queryKey: ['org-promote-student-classes', schoolId],
    queryFn: () => OrgPromotionApi.classesForSchool(schoolId),
    enabled: !!schoolId,
  });
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: subjects = [] } = useQuery({
    queryKey: ['org-lessons-class-subjects', schoolId, classId],
    queryFn: () => OrgReportsApi.classSubjectsForClass(schoolId, classId),
    enabled: !!schoolId && !!classId,
  });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Class" required>
        <Select value={classId} onChange={(e) => onClassChange(e.target.value)} disabled={!schoolId}>
          <option value="">Select class</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Section" required>
        <Select value={sectionId} onChange={(e) => onSectionChange(e.target.value)} disabled={!classId}>
          <option value="">Select section</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Subject" required>
        <Select value={classSubjectId} onChange={(e) => onSubjectChange(e.target.value)} disabled={!classId}>
          <option value="">Select subject</option>
          {subjects.map((s) => (
            <option key={s.classSubjectId} value={s.classSubjectId}>
              {s.subjectName}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

// ───── File attach/list widget — same as OrgLessonsPage's, callbacks wired
// to the topic-scoped add/remove mutations. ─────────────────────────────────
function FilesManager({
  files,
  onAdd,
  adding,
  addError,
  onRemove,
  removingId,
}: {
  files: LessonFile[];
  onAdd: (data: CreateLessonFileData) => void;
  adding: boolean;
  addError?: unknown;
  onRemove: (fileId: string) => void;
  removingId?: string;
}) {
  const [fileType, setFileType] = useState<'document' | 'youtube'>('document');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  async function handleDocumentPick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await UploadApi.upload(file);
      onAdd({ fileType: 'document', fileUrl: res.url, title: title || res.name });
      setTitle('');
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  function handleAddYoutube() {
    if (!youtubeUrl.trim()) return;
    onAdd({ fileType: 'youtube', youtubeUrl: youtubeUrl.trim(), title: title || undefined });
    setYoutubeUrl('');
    setTitle('');
  }

  return (
    <div>
      {files.length === 0 ? (
        <p className="mb-2 text-xs text-slate-400">No files attached yet.</p>
      ) : (
        <ul className="mb-3 divide-y divide-slate-100">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between py-1.5">
              {f.note ? (
                <span className="flex items-center gap-2 text-sm text-amber-700" title={f.note}>
                  <TriangleAlert className="h-4 w-4 shrink-0" />
                  {f.title || 'Attachment'}
                  <span className="text-xs text-amber-600">— {f.note}</span>
                </span>
              ) : (
                <a
                  href={f.fileType === 'youtube' ? f.youtubeUrl ?? '#' : f.fileUrl ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-brand-600 hover:underline"
                >
                  {f.fileType === 'youtube' ? <Youtube className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
                  {f.title || (f.fileType === 'youtube' ? 'YouTube link' : 'Document')}
                  <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
                </a>
              )}
              <button
                type="button"
                title="Remove"
                className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                disabled={removingId === f.id}
                onClick={() => onRemove(f.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <div className="mb-2 flex gap-1">
          <button
            type="button"
            className={`rounded px-2 py-1 text-xs ${fileType === 'document' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setFileType('document')}
          >
            Document
          </button>
          <button
            type="button"
            className={`rounded px-2 py-1 text-xs ${fileType === 'youtube' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setFileType('youtube')}
          >
            YouTube link
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="!w-40 !py-1 text-sm"
          />
          {fileType === 'document' ? (
            <label className="btn-secondary cursor-pointer !py-1 text-xs">
              {uploading ? 'Uploading…' : 'Choose file'}
              <input
                type="file"
                className="hidden"
                disabled={uploading || adding}
                onChange={(e) => e.target.files?.[0] && handleDocumentPick(e.target.files[0])}
              />
            </label>
          ) : (
            <>
              <Input
                placeholder="https://youtube.com/watch?v=…"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="!w-64 !py-1 text-sm"
              />
              <button
                type="button"
                className="btn-secondary flex items-center gap-1 !py-1 text-xs"
                disabled={!youtubeUrl.trim() || adding}
                onClick={handleAddYoutube}
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </>
          )}
        </div>
        {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
        {addError ? <p className="mt-1 text-xs text-red-600">{errMsg(addError)}</p> : null}
      </div>
    </div>
  );
}

// ───── Create topic form — School → Lesson, then class/section/subject
// defaults to the picked lesson's own scope (fetched via lessonDetail),
// same "default but overridable" behavior as the school-tier CreateTopicForm. ─
function CreateTopicForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [classSubjectId, setClassSubjectId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-lesson-topics-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: lessonsForSchool = [] } = useQuery({
    queryKey: ['org-lessons', schoolId, 'for-topic-create'],
    // Single-school picker — wants every lesson for this one school (real
    // volume is small platform-wide, largest single school has 291), not
    // one paginated page, so limit is set high and only `.items` is kept.
    queryFn: () => OrgReportsApi.lessons({ schoolId, limit: 500 }),
    enabled: !!schoolId,
    select: (res) => res.items,
  });

  const { data: lessonDetail } = useQuery({
    queryKey: ['org-lessons', schoolId, lessonId, 'detail-for-topic-default'],
    queryFn: () => OrgReportsApi.lessonDetail(schoolId, lessonId),
    enabled: !!schoolId && !!lessonId,
  });

  useEffect(() => {
    setLessonId('');
    setClassId('');
    setSectionId('');
    setClassSubjectId('');
  }, [schoolId]);

  useEffect(() => {
    if (lessonDetail) {
      setClassId(lessonDetail.classId);
      setSectionId(lessonDetail.sectionId);
      setClassSubjectId(lessonDetail.classSubjectId);
    }
  }, [lessonDetail]);

  const create = useMutation({
    mutationFn: () => {
      const data: CreateOrgLessonTopicData = { schoolId, lessonId, sectionId, classSubjectId, name, description: description || undefined };
      return OrgReportsApi.createLessonTopic(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lesson-topics'] });
      onDone();
    },
  });

  return (
    <div>
      <Field label="School" required className="mb-3">
        <Select value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">Select school</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Lesson" required className="mb-3">
        <Select value={lessonId} onChange={(e) => setLessonId(e.target.value)} disabled={!schoolId}>
          <option value="">Select lesson</option>
          {lessonsForSchool.map((l) => (
            <option key={l.lessonId} value={l.lessonId}>
              {l.lessonName}
            </option>
          ))}
        </Select>
      </Field>
      <OrgClassSectionSubjectPicker
        schoolId={schoolId}
        classId={classId}
        onClassChange={setClassId}
        sectionId={sectionId}
        onSectionChange={setSectionId}
        classSubjectId={classSubjectId}
        onSubjectChange={setClassSubjectId}
      />
      <Field label="Topic name" required className="mt-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Introduction" />
      </Field>
      <Field label="Description" className="mt-3">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </Field>
      {create.error && <p className="mt-2 text-xs text-red-600">{errMsg(create.error)}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={!schoolId || !lessonId || !sectionId || !classSubjectId || !name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create Topic'}
        </button>
      </div>
    </div>
  );
}

// ───── Edit topic form ───────────────────────────────────────────────────
function EditTopicForm({ topic, onDone }: { topic: OrgLessonTopicRow; onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(topic.topicName);
  const [description, setDescription] = useState(topic.description ?? '');

  const update = useMutation({
    mutationFn: () => {
      const data: UpdateOrgLessonTopicData = { name, description: description || undefined };
      return OrgReportsApi.updateLessonTopic(topic.schoolId, topic.topicId, data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lesson-topics'] });
      onDone();
    },
  });

  return (
    <div>
      <Field label="Topic name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description" className="mt-3">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </Field>
      {update.error && <p className="mt-2 text-xs text-red-600">{errMsg(update.error)}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!name.trim() || update.isPending} onClick={() => update.mutate()}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ───── Manage-files detail modal ─────────────────────────────────────────────
function TopicDetail({ schoolId, schoolName, topicId }: { schoolId: string; schoolName: string; topicId: string }) {
  const qc = useQueryClient();
  const { data: detail } = useQuery({
    queryKey: ['org-lesson-topics', schoolId, topicId, 'detail'],
    queryFn: () => OrgReportsApi.lessonTopicDetail(schoolId, topicId),
  });

  const addFile = useMutation({
    mutationFn: (data: CreateLessonFileData) => OrgReportsApi.addLessonTopicFile(schoolId, topicId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lesson-topics', schoolId, topicId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['org-lesson-topics'] });
    },
  });
  const [removingId, setRemovingId] = useState<string | undefined>();
  const removeFile = useMutation({
    mutationFn: (fileId: string) => OrgReportsApi.removeLessonTopicFile(schoolId, topicId, fileId),
    onMutate: (fileId) => setRemovingId(fileId),
    onSettled: () => setRemovingId(undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-lesson-topics', schoolId, topicId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['org-lesson-topics'] });
    },
  });

  if (!detail) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge tone="slate">{schoolName}</Badge>
        <Badge tone="blue">{detail.className}</Badge>
        <Badge tone="slate">{detail.sectionName}</Badge>
        <Badge tone="indigo">{detail.subjectName}</Badge>
      </div>
      {detail.description && <p className="text-sm text-slate-700">{detail.description}</p>}

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Files</h4>
        <FilesManager
          files={detail.files}
          onAdd={(d) => addFile.mutate(d)}
          adding={addFile.isPending}
          addError={addFile.error}
          onRemove={(id) => removeFile.mutate(id)}
          removingId={removingId}
        />
      </div>
    </div>
  );
}

// ───── Page ────────────────────────────────────────────────────────────────
export function OrgLessonTopicsPage() {
  const [schoolId, setSchoolId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const [creating, setCreating] = useState(false);
  const [managing, setManaging] = useState<OrgLessonTopicRow | null>(null);
  const [editing, setEditing] = useState<OrgLessonTopicRow | null>(null);
  const [deleting, setDeleting] = useState<OrgLessonTopicRow | null>(null);
  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20 });

  useEffect(() => {
    setLessonId('');
  }, [schoolId]);

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, lessonId]);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-lesson-topics-report'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: lessonsForSchool = [] } = useQuery({
    queryKey: ['org-lessons', schoolId, 'for-topics-filter'],
    // Single-school picker — wants every lesson for this one school (real
    // volume is small platform-wide, largest single school has 291), not
    // one paginated page, so limit is set high and only `.items` is kept.
    queryFn: () => OrgReportsApi.lessons({ schoolId, limit: 500 }),
    enabled: !!schoolId,
    select: (res) => res.items,
  });

  const filterParams = { schoolId: schoolId || undefined, lessonId: lessonId || undefined };

  const { data, isFetching } = useQuery({
    queryKey: ['org-lesson-topics', dt.params, filterParams],
    queryFn: () => OrgReportsApi.lessonTopics({ ...dt.params, ...filterParams }),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (r: OrgLessonTopicRow) => OrgReportsApi.removeLessonTopic(r.schoolId, r.topicId),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['org-lesson-topics'] });
    },
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportLessonTopics(format, { ...filterParams, search: dt.params.search });
    } finally {
      setBusy(null);
    }
  };

  const COLUMNS = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('schoolName', { header: 'School' }),
    columnHelper.display({
      id: 'topicName',
      header: 'Topic',
      cell: (ctx) => (
        <button type="button" className="text-brand-600 hover:underline" onClick={() => setManaging(ctx.row.original)}>
          {ctx.row.original.topicName}
        </button>
      ),
    }),
    columnHelper.accessor('lessonName', { header: 'Lesson' }),
    columnHelper.accessor('className', { header: 'Class' }),
    columnHelper.accessor('sectionName', { header: 'Section' }),
    columnHelper.accessor('subjectName', { header: 'Subject' }),
    columnHelper.display({
      id: 'fileCount',
      header: 'Files',
      enableSorting: false,
      cell: (ctx) => <Badge tone={ctx.row.original.fileCount > 0 ? 'blue' : 'slate'}>{ctx.row.original.fileCount}</Badge>,
    }),
    columnHelper.display({
      id: 'action',
      header: 'Action',
      enableSorting: false,
      cell: (ctx) => (
        <span className="inline-flex items-center gap-2">
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
            onClick={() => setEditing(ctx.row.original)}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => setDeleting(ctx.row.original)}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </span>
      ),
    }),
  ];

  return (
    <>
      <PageHeader
        title="Lesson Topics"
        description="Real lesson topics across your organization's schools."
        actions={
          <div className="flex items-center gap-2">
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create Topic
            </button>
            <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => runExport('xlsx')}
                disabled={!!busy}
                title="Export to Excel"
              >
                {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
                Excel
              </button>
              <div className="w-px bg-slate-200" />
              <button
                type="button"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => runExport('pdf')}
                disabled={!!busy}
                title="Export to PDF"
              >
                {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
                PDF
              </button>
            </div>
          </div>
        }
      />

      {data?.truncated && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          This organization has more lesson topics than this report can show at once — results are truncated.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by topic name"
            className="w-72 pl-9"
          />
        </div>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        {schoolId && (
          <Select className="!w-64" value={lessonId} onChange={(e) => setLessonId(e.target.value)}>
            <option value="">All lessons</option>
            {lessonsForSchool.map((l) => (
              <option key={l.lessonId} value={l.lessonId}>
                {l.lessonName}
              </option>
            ))}
          </Select>
        )}
      </div>

      <DataTable
        columns={COLUMNS}
        result={data}
        state={dt}
        loading={isFetching}
        emptyMessage="No lesson topics found."
        getRowId={(row) => `${row.schoolId}:${row.topicId}`}
      />

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Topic" size="lg">
        <CreateTopicForm onDone={() => setCreating(false)} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Topic" size="md">
        {editing && <EditTopicForm topic={editing} onDone={() => setEditing(null)} />}
      </Modal>

      <Modal open={!!managing} onClose={() => setManaging(null)} title={managing?.topicName ?? 'Topic'} size="lg">
        {managing && <TopicDetail schoolId={managing.schoolId} schoolName={managing.schoolName} topicId={managing.topicId} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!)}
        title="Delete topic?"
        message={
          remove.error
            ? errMsg(remove.error)
            : `This permanently deletes "${deleting?.topicName}" and its attached files. This cannot be undone.`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </>
  );
}
